import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  PDFPreview,
  Onboarding,
  MainInterface,
  AdminPanel
} from './lazyRoutes';
import { useAuthContext } from '../features/auth/context/AuthContext';
import { hasActiveSubscription } from '../utils/planPermissions';
import PaymentPage from '../features/auth/onboarding/PaymentPage';
import StatusPage from '../components/StatusPage';
import UnsubscribePage from '../components/UnsubscribePage';
import ManageSubscriptionPage from '../components/ManageSubscriptionPage';
import PublicStatusRedirect from '../components/PublicStatusRedirect';
import { getPublicStatusPageOrigin } from '../utils/publicStatusPage';
import ConnectSuccessPage from '../components/ConnectSuccessPage';
import AuthLoader from '../components/AuthLoader';

const PhoneInvitation = lazy(() => import('../features/auth/onboarding/PhoneInvitation'));
const OrganizationSelectorLogin = lazy(() => import('../features/auth/OrganizationSelectorLogin'));

const SuspenseWrapper = ({ children }) => (
  <Suspense fallback={<AuthLoader />}>
    {children}
  </Suspense>
);

export const AppRoutes = () => {
  const location = useLocation();
  
  const {
    isAuthenticated,
    isOnboarded,
    isLoading,
    shouldShowAuthLoader,
    isLoggingOut,
    user,
    handleAuthentication,
    setIsAuthenticated,
    setUser,
    setIsOnboarded,
    checkAuth,
    domainError
  } = useAuthContext();

  // Onboarded org members can use the app (read chats); send is blocked in chat when subscription is inactive.
  const canAccessMainApp = () => {
    if (!user) return false;
    if (hasActiveSubscription(user)) return true;
    if (user.companyId || user.company?.id) return true;
    const plan = user?.company?.subscriptionPlan;
    return plan && ['solo', 'pro', 'max'].includes(plan);
  };

  // New signups without an org still need to pick a plan first
  const hasValidSubscription = () => hasActiveSubscription(user);

  // Helper to check if user has temporary token requiring 2FA
  const hasTemporaryToken = () => {
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      if (!token) return false;
      
      // Decode the token to check for tempAuth flag
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.tempAuth === true;
    } catch (error) {
      return false;
    }
  };


  // Main routing guard - follows the pattern: check loading, then auth, then 2FA, then org selection, then subscription
  const getMainContent = () => {
    console.log('🔍 getMainContent called:', {
      pathname: location.pathname,
      isLoading,
      isAuthenticated,
      isOnboarded,
      hasSubscription: hasValidSubscription()
    });
    
    // Show AuthLoader only during logout or custom domain redirect (not on every load)
    if (shouldShowAuthLoader) {
      return <AuthLoader message={isLoggingOut ? 'Logging out...' : undefined} />;
    }

    // Normal load - show MainInterface with loading state
    if (isLoading) {
      return <MainInterface 
        user={user} 
        refreshUser={checkAuth}
        setIsAuthenticated={setIsAuthenticated}
        setUser={setUser}
        setIsOnboarded={setIsOnboarded}
        isAuthenticated={isAuthenticated}
        isLoading={isLoading}
      />;
    }

    // Not authenticated - redirect to login
    if (!isAuthenticated) {
      if (hasTemporaryToken()) {
        return <Navigate to="/auth/login/2fa" replace />;
      }
      return <Navigate to="/auth/login" replace />;
    }

    // Not onboarded - redirect to onboarding
    if (!isOnboarded) {
      return <Navigate to="/auth/onboarding" replace />;
    }

    // Check if user has temporary token requiring 2FA - redirect to 2FA verification
    // But only if not already on auth pages to prevent infinite redirects
    if (hasTemporaryToken() && !location.pathname.startsWith('/auth')) {
      return <Navigate to="/auth/login/2fa" replace />;
    }

    // Check if user is in the middle of organization selection
    try {
      const authState = JSON.parse(localStorage.getItem('authState') || '{}');
      if (authState.step === 'select-organization' && authState.needsOrgSelection) {
        // User is in org selection flow - redirect to that page
        if (!location.pathname.includes('/auth/login/select-organization')) {
          return <Navigate to="/auth/login/select-organization" replace />;
        }
      }
    } catch (error) {
      console.error('Error checking auth state:', error);
    }

    // No org and no subscription — onboarding payment flow only
    if (!canAccessMainApp()) {
      return <Navigate to="/waiting" replace />;
    }

    // All checks passed - show the main app
    return <MainInterface 
      user={user} 
      refreshUser={checkAuth}
      setIsAuthenticated={setIsAuthenticated}
      setUser={setUser}
      setIsOnboarded={setIsOnboarded}
      isAuthenticated={isAuthenticated}
      isLoading={isLoading}
    />;
  };

  const statusOrigin = getPublicStatusPageOrigin();
  const statusPublicRoutes = statusOrigin ? (
    <>
      <Route path="/status" element={<PublicStatusRedirect segment="" />} />
      <Route path="/status/manage" element={<PublicStatusRedirect segment="manage" />} />
      <Route path="/status/unsubscribe" element={<PublicStatusRedirect segment="unsubscribe" />} />
    </>
  ) : (
    <>
      <Route path="/status" element={<StatusPage />} />
      <Route path="/status/manage" element={<ManageSubscriptionPage />} />
      <Route path="/status/unsubscribe" element={<UnsubscribePage />} />
    </>
  );

  return (
    <div>
      <Routes>
        {/* Public routes that don't require auth */}
        <Route 
          path="/pdf-preview/:chatId" 
          element={
            <SuspenseWrapper>
              <PDFPreview />
            </SuspenseWrapper>
          } 
        />

        {statusPublicRoutes}
        
        {/* OAuth connect success - auto-closes popup after connector connection */}
        <Route 
          path="/connect-success" 
          element={<ConnectSuccessPage />} 
        />
        
        {/* Organization selector for users without orgs */}
        <Route 
          path="/select-organization" 
          element={
            <SuspenseWrapper>
              <div className="flex items-center justify-center min-h-screen bg-white dark:bg-[#030303]">
                <div className="w-full max-w-2xl p-8">
                  <OrganizationSelectorLogin 
                    onOrganizationSelected={() => window.location.href = '/'}
                    onError={(error) => console.error('Org selector error:', error)}
                  />
                </div>
              </div>
            </SuspenseWrapper>
          } 
        />
        
        {/* Auth routes */}
        <Route 
          path="/auth/*" 
          element={
            <SuspenseWrapper>
              {(() => {
                if (domainError) {
                  return null;
                }
                
                // Show AuthLoader only during logout or custom domain redirect
                if (shouldShowAuthLoader) {
                  return <AuthLoader message={isLoggingOut ? 'Logging out...' : undefined} />;
                }
                
                // Check if user is in org selection flow
                const authState = JSON.parse(localStorage.getItem('authState') || '{}');
                const inOrgSelection = authState.step === 'select-organization' && authState.needsOrgSelection;
                
                // Allow auth routes if in org selection, even if authenticated
                if (isAuthenticated && isOnboarded && !hasTemporaryToken() && !inOrgSelection) {
                  return <Navigate to="/" replace />;
                }
                
                return <Onboarding onAuthentication={handleAuthentication} />;
              })()}
            </SuspenseWrapper>
          } 
        />
        
        <Route 
          path="/invitation/:token" 
          element={
            <SuspenseWrapper>
              <PhoneInvitation onAuthentication={handleAuthentication} isLoading={isLoading} />
            </SuspenseWrapper>
          } 
        />
        
        {/* Payment/waiting page */}
        <Route 
          path="/waiting" 
          element={
            <SuspenseWrapper>
              {(() => {
                // Check if this is a payment success redirect
                const urlParams = new URLSearchParams(location.search);
                const hasSuccess = urlParams.get('success') === 'true' || urlParams.get('payment') === 'success';
                const hasSessionId = urlParams.get('session_id');
                const isPaymentReturn = hasSuccess && hasSessionId;
                
                if (isPaymentReturn) {
                  // Clear the URL params and refresh auth data
                  const newUrl = window.location.pathname;
                  window.history.replaceState({}, document.title, newUrl);
                  
                  // Refresh auth to get updated subscription status
                  setTimeout(() => checkAuth(), 1000);
                }
                
                // Show loading during auth check
                if (isLoading) {
                  return <PaymentPage user={user} setUser={setUser} refreshUser={checkAuth} isLoading={isLoading} />;
                }
                
                // Not authenticated - redirect to login
                if (!isAuthenticated || !isOnboarded) {
                  return <Navigate to="/auth/login" replace />;
                }
                
                // Has valid subscription - redirect to main app
                if (hasValidSubscription()) {
                  return <Navigate to="/" replace />;
                }
                
                // Show payment page
                return <PaymentPage user={user} setUser={setUser} refreshUser={checkAuth} />;
              })()}
            </SuspenseWrapper>
          } 
        />
        
        {/* Admin routes */}
        <Route 
          path="/admin/*" 
          element={
            <SuspenseWrapper>
              <AdminPanel />
            </SuspenseWrapper>
          } 
        />
        
        {/* Main app route - uses the guard pattern */}
        <Route 
          path="/*" 
          element={
            <SuspenseWrapper>
              {getMainContent()}
            </SuspenseWrapper>
          } 
        />
      </Routes>
    </div>
  );
}; 